import { createAsyncThunk } from '@reduxjs/toolkit';
import type {
  TApiApplicationDocument,
  TApiApplicationVersionResponse,
  TApiCovenantsResponse,
  TApiRequiredDocumentsResponse,
} from '~/modules/covenants/types.ts';
import type { TThunkError } from '~/store/covenants/types.ts';
import { thunkErrorHandler } from '~/store/utils.ts';
import api from '~/libs/api';
import { RootState } from '~/store/store.ts';

export const fetchRequiredDocuments = createAsyncThunk<
  TApiRequiredDocumentsResponse,
  {
    contragentId: number;
    size: number;
    page: number;
  },
  { state: RootState }
>(
  'covenantsPage/fetchRequiredDocuments',
  async ({ contragentId, size, page }, { rejectWithValue }) => {
    try {
      const { data } = await api.get(
        `/covenants-api/covenants/contragents/${contragentId}/documents/required`,
        {
          params: {
            size,
            page,
          },
        }
      );

      return {
        requiredDocuments: data.required ?? [],
        count: data.count ?? data.required?.length ?? 0,
        pageConfiguration: data.pageConfiguration ?? {
          page,
          size,
          totalElements: data.count ?? data.required?.length ?? 0,
        },
      };
    } catch (error) {
      return thunkErrorHandler(error, rejectWithValue);
    }
  }
);

export const downloadApplicationDocument = createAsyncThunk<
  { data: Blob; filename: string; contentType?: string },
  {
    fileId: string | number;
    isAttached: boolean;
  },
  { rejectValue: TThunkError }
>(
  'covenantsPage/downloadApplicationDocument',
  async ({ fileId, isAttached }, { rejectWithValue }) => {
    try {
      const headers = {
        Accept: '*/*',
      };
      const url = `/covenants-api/covenants/files/download/${fileId}`;
      const response = await api.get<Blob>(url, {
        headers,
        params: {
          isAttached,
        },
        responseType: 'blob',
      });
      const responseContentType = response.headers['content-type'];
      const contentType =
        typeof responseContentType === 'string' && responseContentType.trim()
          ? responseContentType
          : undefined;
      const contentDisposition = response.headers['content-disposition'];
      const filenameMatch = contentDisposition?.match(/filename="(.+)"/);
      const filename = filenameMatch ? filenameMatch[1] : `document_${fileId}`;

      return {
        data: response.data,
        filename,
        contentType,
      };
    } catch (error) {
      return thunkErrorHandler(error, rejectWithValue);
    }
  }
);

export const fetchDocumentCovenants = createAsyncThunk<
  TApiCovenantsResponse,
  {
    contragentId: number;
    documentId: string;
    yearPeriod: string;
    childPeriod: string;
    deadline?: string | null;
    page?: number;
    size?: number;
  },
  { state: RootState }
>(
  'covenantsPage/fetchDocumentCovenants',
  async (
    { contragentId, documentId, yearPeriod, childPeriod, deadline, page = 0, size = 5 },
    { rejectWithValue }
  ) => {
    try {
      const { data } = await api.get<TApiCovenantsResponse>(
        `/covenants-api/covenants/contragents/${contragentId}/documents/${documentId}/covenants`,
        {
          params: {
            yearPeriod,
            childPeriod,
            deadline: deadline ?? 'null',
            page,
            size,
          },
        }
      );

      const covenants = data.covenants ?? [];
      const totalElements = data.pageConfiguration?.totalElements ?? data.count ?? covenants.length;

      return {
        covenants,
        count: data.count ?? totalElements,
        pageConfiguration: data.pageConfiguration ?? {
          page,
          size,
          totalElements,
        },
      };
    } catch (error) {
      return thunkErrorHandler(error, rejectWithValue);
    }
  }
);

export const uploadDocumentSignature = createAsyncThunk<
  TApiApplicationVersionResponse,
  {
    contragentId: number;
    applicationId: string;
    documentId: string;
    signedFile: FormData;
    version: number;
  },
  { state: RootState }
>(
  'covenantsPage/uploadDocumentSignature',
  async ({ contragentId, applicationId, documentId, signedFile, version }, { rejectWithValue }) => {
    try {
      const { data } = await api.post(
        `/covenants-api/covenants/contragents/${contragentId}/applications/${applicationId}/documents/${documentId}/sign`,
        signedFile,
        {
          params: { version },
        }
      );
      return data;
    } catch (error) {
      return thunkErrorHandler(error, rejectWithValue);
    }
  }
);

export const deleteDocumentSignature = createAsyncThunk<
  TApiApplicationVersionResponse,
  {
    contragentId: number;
    applicationId: string;
    documentId: string;
    version: number;
  },
  { state: RootState }
>(
  'covenantsPage/deleteDocumentSignature',
  async ({ contragentId, applicationId, documentId, version }, { rejectWithValue }) => {
    try {
      const { data } = await api.delete(
        `/covenants-api/covenants/contragents/${contragentId}/applications/${applicationId}/documents/${documentId}/sign`,
        {
          params: { version },
        }
      );
      return data;
    } catch (error) {
      return thunkErrorHandler(error, rejectWithValue);
    }
  }
);

export const deleteApplicationDocument = createAsyncThunk<
  TApiApplicationVersionResponse,
  {
    contragentId: number;
    applicationId: string;
    documentId: string;
    baseDocument: TApiApplicationDocument;
    version: number;
  },
  { state: RootState }
>(
  'covenantsPage/deleteApplicationDocument',
  async ({ contragentId, applicationId, documentId, version }, { rejectWithValue }) => {
    try {
      const { data } = await api.delete(
        `/covenants-api/covenants/contragents/${contragentId}/applications/${applicationId}/documents/${documentId}`,
        {
          params: { version },
        }
      );
      return data;
    } catch (error) {
      return thunkErrorHandler(error, rejectWithValue);
    }
  }
);
